/**
 * <media-gallery> — product media gallery.
 * Contract with product-info.js: `setActiveMedia('{section}-{media}', prepend)` and a
 * `ul > li[data-media-id]` list that product-info reorders on variant change.
 *
 * Mobile: horizontal scroll-snap list; an IntersectionObserver keeps the counter/dots in sync.
 * Desktop "thumbnails_left": one active slide (CSS hides the others), thumb rail + arrows.
 * Desktop "stacked": every slide visible; setActiveMedia scrolls the slide into view.
 * Lightbox: native <dialog>, prev/next, 2x click-zoom with transform-origin at the cursor.
 */
if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      connectedCallback() {
        this.list = this.querySelector('ul');
        if (!this.list) return;

        this.thumbs = Array.from(this.querySelectorAll('[data-thumb-for]'));
        this.counter = this.querySelector('.product-gallery__counter [data-current]');
        this.dots = Array.from(this.querySelectorAll('.product-gallery__dot'));
        this.lightbox = this.querySelector('.product-lightbox');
        this.desktop = window.matchMedia('(min-width: 990px)');
        this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

        this.thumbs.forEach((thumb) =>
          thumb.addEventListener('click', () => this.setActiveMedia(thumb.dataset.thumbFor))
        );
        this.querySelector('[data-gallery-prev]')?.addEventListener('click', () => this.step(-1));
        this.querySelector('[data-gallery-next]')?.addEventListener('click', () => this.step(1));

        this.list.addEventListener('click', (event) => {
          const trigger = event.target.closest('[data-gallery-open]');
          if (!trigger) return;
          this.openLightbox(trigger.closest('li[data-media-id]')?.dataset.mediaId);
        });

        this.observeSlides();
        if (this.lightbox) this.initLightbox();
      }

      disconnectedCallback() {
        this.observer?.disconnect();
      }

      get mode() {
        return this.desktop.matches ? this.dataset.desktopLayout || 'thumbnails_left' : 'mobile';
      }

      get slides() {
        return Array.from(this.list.querySelectorAll('li[data-media-id]'));
      }

      get activeSlide() {
        return this.list.querySelector('li.is-active') || this.slides[0];
      }

      get scrollBehavior() {
        return this.reducedMotion.matches ? 'auto' : 'smooth';
      }

      /* Mobile scroll tracking */
      observeSlides() {
        if (!('IntersectionObserver' in window)) return;
        this.observer?.disconnect();
        this.observer = new IntersectionObserver(
          (entries) => {
            if (this.mode !== 'mobile') return;
            entries.forEach((entry) => {
              if (entry.isIntersecting && entry.intersectionRatio >= 0.6) {
                this.markActive(entry.target.dataset.mediaId);
              }
            });
          },
          { root: this.list, threshold: 0.6 }
        );
        this.slides.forEach((slide) => this.observer.observe(slide));
      }

      markActive(mediaId) {
        const slides = this.slides;
        const slide = slides.find((item) => item.dataset.mediaId === mediaId);
        if (!slide) return;

        const previous = this.list.querySelector('li.is-active');
        if (previous && previous !== slide && previous.querySelector('deferred-media[loaded]')) {
          window.pauseAllMedia?.();
        }

        slides.forEach((item) => item.classList.toggle('is-active', item === slide));
        const index = slides.indexOf(slide);

        this.thumbs.forEach((thumb) => {
          const pressed = thumb.dataset.thumbFor === mediaId;
          thumb.setAttribute('aria-pressed', String(pressed));
          if (pressed && this.mode === 'thumbnails_left') {
            thumb.scrollIntoView?.({ block: 'nearest', behavior: this.scrollBehavior });
          }
        });
        this.dots.forEach((dot, i) => dot.classList.toggle('is-active', i === index));
        if (this.counter) this.counter.textContent = index + 1;
      }

      /**
       * Called by product-info.js after it reorders the list (prepend = the variant's
       * featured media was moved to the front) and by thumbs / arrows.
       */
      setActiveMedia(mediaId, prepend = false) {
        const slide = this.list.querySelector(`li[data-media-id="${mediaId}"]`);
        if (!slide) return;

        if (prepend) this.observeSlides();
        this.markActive(mediaId);

        const mode = this.mode;
        if (mode === 'mobile') {
          this.list.scrollTo({
            left: slide.offsetLeft - this.list.offsetLeft,
            behavior: prepend ? 'auto' : this.scrollBehavior,
          });
        } else if (mode === 'stacked' && !prepend) {
          slide.scrollIntoView({ block: 'nearest', behavior: this.scrollBehavior });
        }
        // thumbnails_left: CSS shows the .is-active slide.
      }

      step(direction) {
        const slides = this.slides;
        if (slides.length < 2) return;
        const index = slides.indexOf(this.activeSlide);
        const next = slides[(index + direction + slides.length) % slides.length];
        this.setActiveMedia(next.dataset.mediaId);
      }

      /* ---------- Lightbox ---------- */
      initLightbox() {
        this.lightboxItems = Array.from(this.lightbox.querySelectorAll('[data-lightbox-for]'));
        this.lightboxCurrent = this.lightbox.querySelector('[data-current]');
        const total = this.lightbox.querySelector('[data-total]');
        if (total) total.textContent = this.lightboxItems.length;

        this.lightbox.querySelector('[data-lightbox-close]')?.addEventListener('click', () => this.lightbox.close());
        this.lightbox.querySelector('[data-lightbox-prev]')?.addEventListener('click', () => this.lightboxStep(-1));
        this.lightbox.querySelector('[data-lightbox-next]')?.addEventListener('click', () => this.lightboxStep(1));

        this.lightbox.addEventListener('click', (event) => {
          if (event.target === this.lightbox) this.lightbox.close();
        });
        this.lightbox.addEventListener('keydown', (event) => {
          if (event.key === 'ArrowRight') this.lightboxStep(1);
          if (event.key === 'ArrowLeft') this.lightboxStep(-1);
        });
        this.lightbox.addEventListener('close', () => {
          document.body.classList.remove('overflow-hidden');
          this.resetZoom();
          this.opener?.focus?.();
          this.opener = null;
        });

        this.lightboxItems.forEach((item) => {
          const button = item.querySelector('[data-lightbox-zoom]');
          if (!button) return;
          button.addEventListener('click', (event) => this.toggleZoom(button, event));
          button.addEventListener('pointermove', (event) => {
            if (event.pointerType === 'mouse' && button.getAttribute('aria-pressed') === 'true') {
              this.setZoomOrigin(button, event);
            }
          });
        });
      }

      openLightbox(mediaId) {
        if (!this.lightbox || typeof this.lightbox.showModal !== 'function' || !this.lightboxItems?.length) return;
        let index = this.lightboxItems.findIndex((item) => item.dataset.lightboxFor === mediaId);
        if (index < 0) index = 0;

        this.opener = document.activeElement;
        this.showLightboxItem(index);
        this.lightbox.showModal();
        document.body.classList.add('overflow-hidden');
        this.lightbox.querySelector('[data-lightbox-close]')?.focus();
      }

      showLightboxItem(index) {
        this.resetZoom();
        this.lightboxIndex = index;
        this.lightboxItems.forEach((item, i) => {
          item.hidden = i !== index;
        });
        if (this.lightboxCurrent) this.lightboxCurrent.textContent = index + 1;
        // keep the gallery on the image being inspected so closing lands on it
        const mediaId = this.lightboxItems[index]?.dataset.lightboxFor;
        if (mediaId && this.lightbox.open) this.setActiveMedia(mediaId);
      }

      lightboxStep(direction) {
        const count = this.lightboxItems.length;
        if (count < 2) return;
        this.showLightboxItem((this.lightboxIndex + direction + count) % count);
      }

      toggleZoom(button, event) {
        const zoomed = button.getAttribute('aria-pressed') === 'true';
        if (!zoomed) this.setZoomOrigin(button, event);
        button.setAttribute('aria-pressed', String(!zoomed));
      }

      setZoomOrigin(button, event) {
        const rect = button.getBoundingClientRect();
        if (!rect.width || !rect.height) return;
        const x = Math.min(100, Math.max(0, ((event.clientX - rect.left) / rect.width) * 100));
        const y = Math.min(100, Math.max(0, ((event.clientY - rect.top) / rect.height) * 100));
        button.style.setProperty('--zoom-x', `${x.toFixed(1)}%`);
        button.style.setProperty('--zoom-y', `${y.toFixed(1)}%`);
      }

      resetZoom() {
        this.lightbox
          ?.querySelectorAll('[data-lightbox-zoom][aria-pressed="true"]')
          .forEach((button) => button.setAttribute('aria-pressed', 'false'));
      }
    }
  );
}
